import { ApplicationType, type InterfaceEntity } from '@unifyapps/defs/types/interface';
import type { NavigationForContext } from './navigationForContextTypes';

// shallow spread (was `immer.produce` + `_.set`): we only write a single shallow
// field; immer's proxy + freeze walk over the entire interface record was the
// dominant cost in the context-builder hot path. observable structural sharing
// matches immer's behavior — only the top-level record and `properties` get
// fresh identities; all sibling subtrees retain reference.
export function attachComputedNavigationToInterfaceRecord(
  interfaceRecord: InterfaceEntity,
  navigation: NavigationForContext,
): InterfaceEntity {
  return {
    ...interfaceRecord,
    properties: {
      ...interfaceRecord.properties,
      navigation,
    },
  } as unknown as InterfaceEntity;
}

function getMobileAppProperties(interfaceRecord?: InterfaceEntity, activeAppVariantKey?: string) {
  return {
    appVariants: {
      variants: Object.fromEntries(
        interfaceRecord?.properties.appVariants?.variants.map((variant) => [
          variant.key,
          variant.key,
        ]) ?? [],
      ),
      activeAppVariant: activeAppVariantKey,
    },
  };
}

function getInterfaceRecordForState(
  interfaceRecord?: InterfaceEntity,
  activeAppVariantKey?: string,
) {
  const applicationType = interfaceRecord?.properties.manifest?.type;
  const isMobileApp = applicationType === ApplicationType.MOBILE;

  const interfaceRecordForState = {
    id: interfaceRecord?.id,
    name: interfaceRecord?.properties.name,
    deviceDetails: interfaceRecord?.properties.deviceDetails,
    metadata: {
      logo: interfaceRecord?.properties.metadata.logo,
    },
    ...(isMobileApp && {
      ...getMobileAppProperties(interfaceRecord, activeAppVariantKey),
    }),
    navigation: {
      activeItem: { activeGroupId: null, activeItemId: null },
      ...interfaceRecord?.properties.navigation,
    },
  };

  return interfaceRecordForState;
}

export type InterfaceRecordState = ReturnType<typeof getInterfaceRecordForState>;

export default getInterfaceRecordForState;
